/**
 * src/modules/dev-sandbox.js — H4 rehecho v2 (26-may-2026).
 *
 * Ruta oculta /#/dev-sandbox para QA visual sin Supabase. NO se linkea
 * desde ningún menú. Vive permanentemente en el código como herramienta
 * del dueño para validar el diseño de la card de predicción de playoff
 * antes de migrarla al home real.
 *
 * 3 SECCIONES apiladas:
 *   1. Card de fase de grupos (referencia, formato HOME — 3 botones).
 *   2. Card de playoff en FORMATO HOME — misma estructura exterior que (1)
 *      pero el bloque central tiene 2 INPUTS INLINE entre las banderas y
 *      códigos (sin spinners). Si empate: selector "¿Quién pasa por
 *      penales?" debajo. Botón Guardar abajo.
 *   3. Card de playoff en FORMATO PRODE PLAYOFFS — fila compacta clonada
 *      visualmente de prode.js renderMatchCard (DT-DQ: CSS de prode.js
 *      vive en Shadow DOM, replicado inline con prefijo .sb-match-*).
 *      META + STATUS arriba; teams grid 7 cols (nombre · flag · input ·
 *      − · input · flag · nombre); prediction-line si hay valor; foot
 *      (max-points + Ver puntos + JUGAR/EDITAR).
 *
 * Las 3 cards son MOCK. Botones de predicción muestran alert con el
 * payload que SE HUBIERA enviado a la RPC. No tocan Supabase.
 *
 * DT-DP: HTML/CSS de la sección 1 está duplicado de grupo.js
 * buildPartidoCards (vive en closure, no exportado). Unificar post-piloto.
 * DT-DQ: HTML/CSS de la sección 3 está clonado de prode.js renderMatchCard
 * pero replicado inline porque el CSS vive en Shadow DOM aislado.
 */

import { appRoot } from '../utils/dom.js';

// ─── Validación de predicción playoff (espejo de prode.js) ─────────────────
const isPlayoffPredictionReady = (pred) => {
    if (!pred) return false;
    if (pred.goalsHome === '' || pred.goalsAway === '') return false;
    if (pred.goalsHome === undefined || pred.goalsAway === undefined) return false;
    const home = Number(pred.goalsHome);
    const away = Number(pred.goalsAway);
    if (!Number.isFinite(home) || !Number.isFinite(away)) return false;
    if (home === away && !pred.clasificado_codigo) return false;
    return true;
};

// ─── Mocks ─────────────────────────────────────────────────────────────────

const MOCK_GRUPO = {
    partido_id: 'mock-grupo-mex-rsa',
    fase: 'grupo',
    grupo_mundial: 'A',
    home: { code: 'MEX', name: 'Mexico', flag: '🇲🇽' },
    away: { code: 'RSA', name: 'South Africa', flag: '🇿🇦' },
};

const MOCK_PLAYOFF = {
    partido_id: 'mock-octavos-arg-bra',
    fase: 'octavos',
    home: { code: 'ARG', name: 'Argentina', flag: '🇦🇷' },
    away: { code: 'BRA', name: 'Brasil', flag: '🇧🇷' },
};

const SCORING_GRUPO = [
    ['Acierto ganador', '+5'],
    ['Empate acertado', '+1'],
    ['Error leve', '0'],
    ['Error contrario', '-1'],
];
const SCORING_PLAYOFF = [
    ['Clasificado', '+10'],
    ['Diferencia', '+10'],
    ['Goles del ganador', '+10'],
    ['Acierto completo', '+50'],
    ['Multiplicador Octavos', '×2'],
    ['Bonus Argentina', '×3'],
];

// ─── CSS local del sandbox (selectores prefijados .sb-*) ───────────────────

const SANDBOX_CSS = `
    .sb-header { background: linear-gradient(135deg, #dc2626, #991b1b); color: #fff; padding: 0.8rem 1rem; border-radius: 8px; text-align: center; margin-bottom: 1.2rem; font-weight: 900; letter-spacing: 0.5px; text-transform: uppercase; font-size: 0.85rem; }
    .sb-section-title { color: var(--white); font-family: var(--font-heading); font-size: 1.05rem; margin: 1.4rem 0 0.6rem; text-transform: uppercase; letter-spacing: 0.5px; }
    .sb-section-title:first-of-type { margin-top: 0; }

    /* Quita spinners en inputs number (Chrome/Safari/Firefox) */
    .sb-wrap input[type="number"]::-webkit-inner-spin-button,
    .sb-wrap input[type="number"]::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }
    .sb-wrap input[type="number"] { -moz-appearance: textfield; appearance: textfield; }

    /* ─── Card formato HOME (secciones 1 y 2) ────────────────────────── */
    .sb-card { position: relative; padding: 1.6rem 1.2rem 1.2rem; border: 1px solid rgba(255,255,255,0.18); background: rgba(255,255,255,0.03); border-radius: 12px; display: flex; flex-direction: column; max-width: 360px; margin: 0 auto; }
    .sb-badge { font-size: 0.6rem; font-weight: 800; letter-spacing: 0.5px; padding: 0.18rem 0.45rem; border-radius: 3px; text-transform: uppercase; position: absolute; top: 0.6rem; right: 0.7rem; background: rgba(117,170,219,0.18); color: var(--celeste); border: 1px solid rgba(117,170,219,0.3); }
    .sb-ctx-line { text-align: center; font-size: 0.7rem; color: var(--text-muted); letter-spacing: 1px; font-weight: 700; margin-bottom: 0.8rem; margin-top: 0.4rem; }

    /* Sección 1 — teams "flag + code · vs · flag + code" */
    .sb-teams { text-align: center; margin-bottom: 0.7rem; display: flex; justify-content: center; align-items: center; gap: 1rem; }
    .sb-team { display: flex; flex-direction: column; align-items: center; gap: 0.3rem; }
    .sb-team .flag { font-size: 2rem; }
    .sb-team .code { font-size: 1.2rem; font-family: var(--font-heading); color: var(--white); }
    .sb-vs { color: var(--text-muted); font-size: 1rem; margin: 0 0.5rem; }

    /* Sección 1 — botones gana/empate/gana */
    .sb-pred-btn { flex: 1; padding: 0.8rem 0.2rem; font-size: 0.75rem; margin: 0; background: rgba(255,255,255,0.08); color: var(--white); font-weight: 500; border: 1px solid rgba(255,255,255,0.18); border-radius: 6px; cursor: pointer; }
    .sb-pred-btn.selected { background: var(--celeste); color: #000; font-weight: 700; }
    .sb-pred-row { display: flex; gap: 0.3rem; margin-top: 1rem; }

    /* Sección 2 — teams CON inputs inline entre los códigos */
    .sb-teams-inline { display: grid; grid-template-columns: auto auto auto auto auto auto auto; align-items: center; column-gap: 0.5rem; justify-content: center; margin: 0.4rem 0 0.9rem; }
    .sb-teams-inline .flag { font-size: 1.8rem; }
    .sb-teams-inline .code { font-family: var(--font-heading); color: var(--white); font-size: 1rem; }
    .sb-teams-inline .sb-inline-input { width: 50px; padding: 0.45rem 0.2rem; font-size: 1.35rem; text-align: center; border: 1px solid rgba(255,255,255,0.2); border-radius: 8px; background: rgba(255,255,255,0.05); color: var(--white); font-weight: 900; font-family: var(--font-heading); }
    .sb-teams-inline .sb-inline-input:focus { outline: 2px solid var(--celeste); border-color: var(--celeste); }
    .sb-teams-inline .sb-inline-sep { color: var(--text-muted); font-size: 1.4rem; font-weight: 900; }

    /* Bloque empate (sec 2 + sec 3) */
    .sb-pen-block { display: none; margin-top: 0.7rem; padding-top: 0.6rem; border-top: 1px solid rgba(255,255,255,0.08); }
    .sb-pen-block.visible { display: block; }
    .sb-pen-title { display: block; color: var(--white); font-size: 0.85rem; font-weight: 700; margin-bottom: 0.45rem; text-align: center; }
    .sb-pen-row { display: flex; gap: 0.5rem; justify-content: center; }
    .sb-pen-chip { flex: 0 1 auto; padding: 0.45rem 0.9rem; font-size: 0.8rem; background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.18); border-radius: 999px; color: var(--white); cursor: pointer; }
    .sb-pen-chip.selected { background: var(--celeste); border-color: var(--celeste); color: #000; font-weight: 800; }

    /* Botón Guardar (sec 2 — formato home) */
    .sb-save { width: 100%; margin-top: 1rem; padding: 0.75rem; font-size: 0.9rem; font-weight: 800; background: linear-gradient(135deg, var(--celeste), #a855f7); color: #fff; border: none; border-radius: 8px; cursor: pointer; letter-spacing: 0.3px; }
    .sb-save:disabled { background: rgba(255,255,255,0.08); color: rgba(255,255,255,0.4); cursor: not-allowed; }
    /* Modo "saved" del botón — look sobrio igual al EDITAR de card 3 */
    .sb-save.editing { background: transparent; color: #bbf7d0; border: 1px solid rgba(61,220,132,.28); font-weight: 900; padding: 0.55rem; }
    .sb-save.editing:hover { background: rgba(61,220,132,.08); border-color: rgba(61,220,132,.42); }

    /* Línea de resumen post-guardado (sec 2) */
    .sb-saved-line { display: none; margin-top: 0.7rem; padding: 0.5rem 0.7rem; border-radius: 8px; background: rgba(117,170,219,.07); border: 1px solid rgba(117,170,219,.18); color: rgba(226,232,240,.94); font-size: 0.85rem; font-weight: 700; text-align: center; }
    .sb-saved-line.visible { display: block; }
    .sb-saved-line strong { color: #bbf7d0; font-weight: 900; }

    /* Error */
    .sb-error { display: none; margin-top: 0.6rem; padding: 0.45rem; border-radius: 6px; background: rgba(255,107,107,0.1); border: 1px solid rgba(255,107,107,0.3); color: #ff6b6b; font-size: 0.78rem; text-align: center; }
    .sb-error.visible { display: block; }

    /* "Hasta X pts" + acordeón scoring (compartido sec 1, 2 y 3) */
    .sb-pts-row { display: grid; grid-template-columns: 1fr auto 1fr; align-items: center; margin-top: 0.8rem; }
    .sb-pts-label { color: rgba(187,247,208,.75); font-size: 9.5px; font-weight: 600; white-space: nowrap; justify-self: start; letter-spacing: 0.2px; }
    .sb-pts-toggle { justify-self: center; min-height: 24px; padding: 0 9px; border: 1px solid rgba(255,255,255,.1); border-radius: 999px; color: rgba(226,232,240,.85); background: rgba(255,255,255,.03); font-size: 10px; font-weight: 800; cursor: pointer; }
    .sb-pts-toggle.active { border-color: rgba(117,170,219,.2); background: rgba(117,170,219,.07); color: rgba(226,232,240,.95); }
    .sb-scoring-wrap { display: none; width: fit-content; min-width: 220px; margin: 0.5rem auto 0; border: 1px solid rgba(117,170,219,.18); border-radius: 9px; background: rgba(117,170,219,.05); padding: 8px 12px; }
    .sb-scoring-wrap.visible { display: block; }
    .sb-scoring-table { display: grid; grid-template-columns: max-content 50px; column-gap: 14px; row-gap: 5px; justify-content: center; color: rgba(148,163,184,.85); font-size: 11px; }
    .sb-scoring-label { text-align: right; }
    .sb-scoring-value { color: rgba(248,250,252,.95); text-align: right; font-weight: 900; }

    /* ─── Card formato PRODE PLAYOFFS (sección 3) ────────────────────── */
    /* DT-DQ: clonado de src/modules/prode.js renderMatchCard (Shadow DOM). */
    .sb-match { border: 1px solid rgba(255,255,255,.105); border-radius: 11px; background: linear-gradient(135deg, rgba(255,255,255,.06), rgba(255,255,255,.025)); overflow: hidden; max-width: 460px; margin: 0 auto; }
    .sb-match-head { padding: 9px 10px 9px; }
    .sb-match-topline { display: flex; align-items: flex-start; justify-content: space-between; gap: 10px; margin-bottom: 7px; }
    .sb-match-meta { min-width: 0; color: rgba(148,163,184,.8); font-size: 9.5px; font-weight: 900; letter-spacing: .04em; text-transform: uppercase; white-space: nowrap; }
    .sb-match-meta strong { color: rgba(248,250,252,.78); font-weight: 900; }
    .sb-match-status { flex: 0 0 auto; color: rgba(248,250,252,.62); font-size: 9.5px; font-weight: 900; letter-spacing: .04em; text-transform: uppercase; }
    .sb-match-teams { display: grid; grid-template-columns: minmax(60px,1fr) 28px 38px 14px 38px 28px minmax(60px,1fr); align-items: center; column-gap: 5px; width: 100%; margin: 0 auto; }
    .sb-match-team-name { min-width: 0; color: var(--white); font-size: 13px; font-weight: 900; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .sb-match-team-name.home { text-align: right; }
    .sb-match-team-name.away { text-align: left; }
    .sb-match-flag-code { flex: 0 0 auto; min-width: 30px; height: 22px; display: inline-flex; align-items: center; justify-content: center; border-radius: 5px; border: 1px solid rgba(117,170,219,.18); color: var(--celeste); background: rgba(117,170,219,.07); font-size: 9.5px; font-weight: 900; padding: 0 4px; }
    .sb-match-flag-emoji { display: inline-flex; align-items: center; justify-content: center; font-size: 22px; line-height: 1; }
    .sb-match-goal-input { width: 38px; padding: 5px 2px; font-size: 17px; text-align: center; border: 1px solid rgba(255,255,255,.18); border-radius: 7px; background: rgba(255,255,255,.05); color: var(--white); font-weight: 900; font-family: var(--font-heading); }
    .sb-match-goal-input:focus { outline: 2px solid var(--celeste); border-color: var(--celeste); }
    .sb-match-goal-sep { text-align: center; color: rgba(148,163,184,.78); font-size: 14px; font-weight: 900; }
    .sb-match-pred-line { display: none; margin: 6px auto 0; color: rgba(148,163,184,.9); font-size: 11px; font-weight: 800; text-align: center; }
    .sb-match-pred-line.visible { display: block; }
    .sb-match-pred-line strong { color: rgba(248,250,252,.92); font-weight: 900; }
    .sb-match-foot { display: grid; grid-template-columns: minmax(0,1fr) auto minmax(0,1fr); align-items: center; gap: 7px; margin-top: 7px; min-height: 26px; }
    .sb-match-foot .sb-pts-label { justify-self: start; }
    .sb-match-foot .sb-pts-toggle { justify-self: center; }
    .sb-match-cta { justify-self: end; min-height: 26px; padding: 0 12px; border: 1px solid rgba(61,220,132,.22); border-radius: 999px; color: #bbf7d0; background: rgba(61,220,132,.09); font-size: 10px; font-weight: 900; cursor: pointer; letter-spacing: 0.4px; }
    .sb-match-cta:hover { background: rgba(61,220,132,.15); border-color: rgba(61,220,132,.34); }
    .sb-match-pen-block { display: none; margin-top: 8px; padding-top: 8px; border-top: 1px solid rgba(255,255,255,.07); }
    .sb-match-pen-block.visible { display: block; }
    .sb-match-scoring-wrap { display: none; margin: 0 10px 10px; padding: 8px 12px; border: 1px solid rgba(117,170,219,.18); border-radius: 9px; background: rgba(117,170,219,.05); }
    .sb-match-scoring-wrap.visible { display: block; }
    .sb-match-error { display: none; margin: 0 10px 8px; padding: 0.45rem; border-radius: 6px; background: rgba(255,107,107,0.1); border: 1px solid rgba(255,107,107,0.3); color: #ff6b6b; font-size: 0.78rem; text-align: center; }
    .sb-match-error.visible { display: block; }
`;

// ─── Sección 1: card grupo (clon del home, formato 3 botones) ──────────────

const renderSeccion1 = () => `
    <h2 class="sb-section-title">1. Card grupo (referencia, igual al home actual)</h2>
    <div class="sb-card" id="sb-grupo">
        <span class="sb-badge">PROGRAMADO</span>
        <div class="sb-ctx-line">GRUPO ${MOCK_GRUPO.grupo_mundial} · 11 JUN · 16:00</div>
        <div class="sb-teams">
            <div class="sb-team">
                <span class="flag">${MOCK_GRUPO.home.flag}</span>
                <span class="code">${MOCK_GRUPO.home.code}</span>
            </div>
            <span class="sb-vs">vs</span>
            <div class="sb-team">
                <span class="flag">${MOCK_GRUPO.away.flag}</span>
                <span class="code">${MOCK_GRUPO.away.code}</span>
            </div>
        </div>
        <div class="sb-pred-row">
            <button class="sb-pred-btn" data-grupo-pick="home">${MOCK_GRUPO.home.flag} Gana ${MOCK_GRUPO.home.name}</button>
            <button class="sb-pred-btn" data-grupo-pick="draw">Empate</button>
            <button class="sb-pred-btn" data-grupo-pick="away">${MOCK_GRUPO.away.flag} Gana ${MOCK_GRUPO.away.name}</button>
        </div>
        <div class="sb-pts-row">
            <span class="sb-pts-label">Hasta 5 pts</span>
            <button class="sb-pts-toggle" data-toggle="grupo">Ver puntos en juego</button>
            <span></span>
        </div>
        <div class="sb-scoring-wrap" data-scoring="grupo">
            <div class="sb-scoring-table">
                ${SCORING_GRUPO.map(([l, v]) => `<span class="sb-scoring-label">${l}</span><span class="sb-scoring-value">${v}</span>`).join('')}
            </div>
        </div>
    </div>
`;

// ─── Sección 2: card playoff (formato HOME — inputs inline entre banderas) ─

const renderSeccion2 = () => `
    <h2 class="sb-section-title">2. Card playoff (formato home del grupo)</h2>
    <div class="sb-card" id="sb-playoff-home">
        <span class="sb-badge">PROGRAMADO</span>
        <div class="sb-ctx-line">OCTAVOS · 29 JUN · 21:00</div>
        <div class="sb-teams-inline">
            <span class="code">${MOCK_PLAYOFF.home.code}</span>
            <span class="flag">${MOCK_PLAYOFF.home.flag}</span>
            <input class="sb-inline-input" type="number" min="0" max="15" inputmode="numeric" data-goals="home" data-scope="home-card">
            <span class="sb-inline-sep">−</span>
            <input class="sb-inline-input" type="number" min="0" max="15" inputmode="numeric" data-goals="away" data-scope="home-card">
            <span class="flag">${MOCK_PLAYOFF.away.flag}</span>
            <span class="code">${MOCK_PLAYOFF.away.code}</span>
        </div>
        <div class="sb-pen-block" data-pen-block="home-card">
            <strong class="sb-pen-title">¿Quién pasa por penales?</strong>
            <div class="sb-pen-row">
                <button class="sb-pen-chip" data-pen="ARG" data-scope="home-card">${MOCK_PLAYOFF.home.flag} ARG</button>
                <button class="sb-pen-chip" data-pen="BRA" data-scope="home-card">${MOCK_PLAYOFF.away.flag} BRA</button>
            </div>
        </div>
        <div class="sb-error" data-error="home-card"></div>
        <div class="sb-saved-line" data-saved-line="home-card"></div>
        <button class="sb-save" data-save="home-card" disabled>Guardar predicción</button>
        <div class="sb-pts-row">
            <span class="sb-pts-label">Hasta 480 pts</span>
            <button class="sb-pts-toggle" data-toggle="playoff-home">Ver puntos en juego</button>
            <span></span>
        </div>
        <div class="sb-scoring-wrap" data-scoring="playoff-home">
            <div class="sb-scoring-table">
                ${SCORING_PLAYOFF.map(([l, v]) => `<span class="sb-scoring-label">${l}</span><span class="sb-scoring-value">${v}</span>`).join('')}
            </div>
        </div>
    </div>
`;

// ─── Sección 3: card playoff (formato Prode Playoffs — fila compacta) ──────

const renderSeccion3 = () => `
    <h2 class="sb-section-title">3. Card playoff (formato Prode Playoffs)</h2>
    <div class="sb-match" id="sb-playoff-prode">
        <div class="sb-match-head">
            <div class="sb-match-topline">
                <div class="sb-match-meta">Octavos &middot; 29 JUN &middot; <strong>21:00</strong></div>
                <div class="sb-match-status"><span>PROGRAMADO</span></div>
            </div>
            <div class="sb-match-teams">
                <span class="sb-match-team-name home">${MOCK_PLAYOFF.home.name}</span>
                <span class="sb-match-flag-emoji">${MOCK_PLAYOFF.home.flag}</span>
                <input class="sb-match-goal-input" type="number" min="0" max="15" inputmode="numeric" data-goals="home" data-scope="prode-card">
                <span class="sb-match-goal-sep">−</span>
                <input class="sb-match-goal-input" type="number" min="0" max="15" inputmode="numeric" data-goals="away" data-scope="prode-card">
                <span class="sb-match-flag-emoji">${MOCK_PLAYOFF.away.flag}</span>
                <span class="sb-match-team-name away">${MOCK_PLAYOFF.away.name}</span>
            </div>
            <div class="sb-match-pred-line" data-pred-line="prode-card"></div>
            <div class="sb-match-pen-block" data-pen-block="prode-card">
                <strong class="sb-pen-title">¿Quién pasa por penales?</strong>
                <div class="sb-pen-row">
                    <button class="sb-pen-chip" data-pen="ARG" data-scope="prode-card">${MOCK_PLAYOFF.home.flag} Argentina</button>
                    <button class="sb-pen-chip" data-pen="BRA" data-scope="prode-card">${MOCK_PLAYOFF.away.flag} Brasil</button>
                </div>
            </div>
            <div class="sb-match-foot">
                <span class="sb-pts-label">Hasta 480 pts</span>
                <button class="sb-pts-toggle" data-toggle="playoff-prode">Ver puntos en juego</button>
                <button class="sb-match-cta" data-save="prode-card" data-cta-label="JUGAR">JUGAR</button>
            </div>
        </div>
        <div class="sb-match-error" data-error="prode-card"></div>
        <div class="sb-match-scoring-wrap" data-scoring="playoff-prode">
            <div class="sb-scoring-table">
                ${SCORING_PLAYOFF.map(([l, v]) => `<span class="sb-scoring-label">${l}</span><span class="sb-scoring-value">${v}</span>`).join('')}
            </div>
        </div>
    </div>
`;

// ─── Render principal + wiring ────────────────────────────────────────────

export const renderDevSandbox = () => {
    if (!appRoot) return;

    appRoot.innerHTML = `
        <style>${SANDBOX_CSS}</style>
        <div class="sb-wrap" style="width:100%; padding:1rem 0; max-width:520px; margin:0 auto;">
            <div class="sb-header">DEV SANDBOX — Mock visual de cards predicción · NO PRODUCCIÓN</div>
            ${renderSeccion1()}
            ${renderSeccion2()}
            ${renderSeccion3()}
        </div>
    `;

    // ─── Sección 1 — picks de grupo (alert al click) ────────────────────
    document.querySelectorAll('[data-grupo-pick]').forEach((btn) => {
        btn.addEventListener('click', () => {
            const pick = btn.getAttribute('data-grupo-pick');
            document.querySelectorAll('[data-grupo-pick]').forEach((b) => b.classList.toggle('selected', b === btn));
            const label = pick === 'home' ? `Gana ${MOCK_GRUPO.home.name}`
                : pick === 'away' ? `Gana ${MOCK_GRUPO.away.name}` : 'Empate';
            alert(`[sandbox · grupo] pick: ${label}\n\nEn producción esto llamaría a guardar_prediccion con prediction_type='group_match'.`);
        });
    });

    // ─── Acordeones "Ver puntos en juego" (3 cards) ─────────────────────
    document.querySelectorAll('[data-toggle]').forEach((btn) => {
        btn.addEventListener('click', () => {
            const key = btn.getAttribute('data-toggle');
            const wrap = document.querySelector(`[data-scoring="${key}"]`);
            const isOpen = wrap?.classList.toggle('visible');
            btn.classList.toggle('active', !!isOpen);
        });
    });

    // ─── Wiring de cards playoff (scope = 'home-card' | 'prode-card') ───
    const wirePlayoffCard = (scope) => {
        const pred = { goalsHome: '', goalsAway: '', clasificado_codigo: null, saved: false };
        const inputs = document.querySelectorAll(`input[data-scope="${scope}"][data-goals]`);
        const penBlock = document.querySelector(`[data-pen-block="${scope}"]`);
        const errorEl = document.querySelector(`[data-error="${scope}"]`);
        const saveBtn = document.querySelector(`[data-save="${scope}"]`);
        const penChips = document.querySelectorAll(`[data-pen][data-scope="${scope}"]`);
        const predLine = document.querySelector(`[data-pred-line="${scope}"]`);   // sec 3
        const savedLine = document.querySelector(`[data-saved-line="${scope}"]`); // sec 2
        const isProdeCard = scope === 'prode-card';

        const labelPred = () => {
            const h = Number(pred.goalsHome);
            const a = Number(pred.goalsAway);
            if (!Number.isFinite(h) || !Number.isFinite(a)) return '';
            let base = `${h}-${a}`;
            if (h === a && pred.clasificado_codigo) {
                const equipo = pred.clasificado_codigo === MOCK_PLAYOFF.home.code
                    ? MOCK_PLAYOFF.home.name : MOCK_PLAYOFF.away.name;
                base += ` (pasa ${equipo})`;
            }
            return base;
        };

        const refresh = () => {
            const ambos = pred.goalsHome !== '' && pred.goalsAway !== '';
            const isTie = ambos && Number(pred.goalsHome) === Number(pred.goalsAway);

            // Bloque penales: solo en modo edición (no-saved) y si empate.
            penBlock?.classList.toggle('visible', isTie && !pred.saved);
            if (!isTie) pred.clasificado_codigo = null;
            penChips.forEach((c) => c.classList.toggle('selected', c.getAttribute('data-pen') === pred.clasificado_codigo));

            const ready = isPlayoffPredictionReady(pred);

            if (isProdeCard) {
                // CTA dinámico: saved → EDITAR; sin saved con datos → GUARDAR; vacío → JUGAR.
                const hasAny = pred.goalsHome !== '' || pred.goalsAway !== '';
                if (saveBtn) {
                    let targetLabel;
                    if (pred.saved) targetLabel = 'EDITAR';
                    else if (hasAny) targetLabel = 'GUARDAR';
                    else targetLabel = 'JUGAR';
                    if (saveBtn.getAttribute('data-cta-label') !== targetLabel) {
                        saveBtn.textContent = targetLabel;
                        saveBtn.setAttribute('data-cta-label', targetLabel);
                    }
                }
                // pred-line: visible si ready (saved o no — preview en edición + confirmación post-saved).
                if (predLine) {
                    if (ready) {
                        predLine.innerHTML = `Jugaste: <strong>${labelPred()}</strong>`;
                        predLine.classList.add('visible');
                    } else {
                        predLine.classList.remove('visible');
                        predLine.innerHTML = '';
                    }
                }
            } else {
                // Sec 2 — formato home.
                if (saveBtn) {
                    if (pred.saved) {
                        saveBtn.disabled = false;
                        saveBtn.classList.add('editing');
                        saveBtn.textContent = 'Editar predicción';
                    } else {
                        saveBtn.classList.remove('editing');
                        saveBtn.textContent = 'Guardar predicción';
                        saveBtn.disabled = !ready;
                    }
                }
                if (savedLine) {
                    if (pred.saved && ready) {
                        savedLine.innerHTML = `Jugaste: <strong>${labelPred()}</strong>`;
                        savedLine.classList.add('visible');
                    } else {
                        savedLine.classList.remove('visible');
                        savedLine.innerHTML = '';
                    }
                }
            }

            if (errorEl) errorEl.classList.remove('visible');
        };

        // Inputs / chips: cualquier edición vuelve a modo edición (saved=false).
        inputs.forEach((el) => {
            el.addEventListener('input', (e) => {
                if (el.dataset.goals === 'home') pred.goalsHome = e.target.value;
                if (el.dataset.goals === 'away') pred.goalsAway = e.target.value;
                pred.saved = false;
                refresh();
            });
        });

        penChips.forEach((chip) => {
            chip.addEventListener('click', () => {
                pred.clasificado_codigo = chip.getAttribute('data-pen');
                pred.saved = false;
                refresh();
            });
        });

        saveBtn?.addEventListener('click', () => {
            // Si ya está guardado, click → volver a modo edición.
            if (pred.saved) {
                pred.saved = false;
                refresh();
                return;
            }
            // Validación + guardado.
            if (!isPlayoffPredictionReady(pred)) {
                if (errorEl) {
                    const ambos = pred.goalsHome !== '' && pred.goalsAway !== '';
                    errorEl.textContent = ambos
                        ? 'Empate sin clasificado: elegí quién pasa por penales.'
                        : 'Cargá los goles de ambos equipos.';
                    errorEl.classList.add('visible');
                }
                return;
            }
            pred.saved = true;
            refresh();
            const payload = {
                partido_id: MOCK_PLAYOFF.partido_id,
                prediction_type: 'playoff_match',
                score_local: Number(pred.goalsHome),
                score_visitante: Number(pred.goalsAway),
                clasificado_codigo: pred.clasificado_codigo,
            };
            console.log(`[dev-sandbox · ${scope}] payload:`, payload);
            alert(`[sandbox · ${scope}] payload que SE HUBIERA enviado:\n\n${JSON.stringify(payload, null, 2)}`);
        });
    };

    wirePlayoffCard('home-card');
    wirePlayoffCard('prode-card');
};
